/** @jsxImportSource @emotion/react */
import { useEffect } from 'react';
import { css } from '@emotion/react';
import { BORDER_COLOR } from '../../shared/typings';
import { ChatProfileAvatar } from '../home/v1/atoms/chat-profile-avatar';
import { ChatType } from '../home/v1/components/chat-list-section/chat-row/typings';
import { ReactComponent as RightChevron } from '../../shared/assets/svg/chevron-right.svg';
import { ButtonBase, Switch } from '@mui/material';
import { ReactComponent as UserIcon } from '../../shared/assets/svg/user-icon.svg';
import { ReactComponent as LogoutIcon } from '../../shared/assets/svg/logout-icon.svg';
import { useAgentStatus } from './hooks/useAgentStatus';
import { useAtom } from 'jotai';
import { showLogoutModalAtom } from '../../shared/states/login';
import LogoutModal from '../../shared/components/organisms/logout-modal';
import { useCustomNavigate } from '../../shared/hooks/use-conversation-navigate';
import { useProfile } from '../../shared/hooks/use-profile';
import UserProfileIcon from '../../shared/icons/icons-revamp/user_profile';
import { QSTypography } from '../../shared/components/atoms';
import { StatusIndicator, STATUS_COLORS as statusColor } from '../../shared/components/atoms/status-indicator';
import ArrowExpandIcon from '../../shared/icons/ArrowExpand';
import { ROUTES } from '../../shared/constants/routes';
import { AgentStatus } from './typings';

interface AgentAvailabilitySidebarProps {
  showExpandedView: boolean;
  onCollapseSidebar?: () => void;
  shouldShowAgentAvailability?: boolean;
}

export default function AgentAvailabilitySidebar({
  showExpandedView,
  onCollapseSidebar,
  shouldShowAgentAvailability,
}: AgentAvailabilitySidebarProps) {
  const { isOnline, handleOpenMoreMenu, changeStatus, isMoreMenuOpen, setIsMoreMenuOpen, setIsOnline } =
    useAgentStatus();
  const userProfile = useProfile();
  const name = userProfile?.name || '';
  const phoneNumber = userProfile?.phone || '';
  const navigate = useCustomNavigate();
  const [showLogoutModal, setShowLogoutModal] = useAtom(showLogoutModalAtom);

  useEffect(() => {
    if (userProfile?.status) {
      setIsOnline(userProfile.status === AgentStatus.ONLINE);
    }
  }, [userProfile?.status, setIsOnline]);

  const handleEditProfile = () => {
    setIsMoreMenuOpen(false);
    onCollapseSidebar?.();
    navigate({ pathname: `${ROUTES.SETTINGS}${ROUTES.SETTINGS_PROFILE}` });
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  return (
    <>
      <ButtonBase
        onClick={() => handleOpenMoreMenu()}
        css={css({
          display: 'flex',
          gap: '12px',
          width: '100%',
          borderTop: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 10px',
        })}
      >
        <div
          css={css({
            position: 'relative',
          })}
        >
          <ChatProfileAvatar
            chatType={ChatType.INDIVIDUAL}
            name={name}
            phoneNumber={phoneNumber}
            styles={{
              height: 36,
              width: 36,
              margin: 0,
            }}
          />
          {shouldShowAgentAvailability && (
            <StatusIndicator status={isOnline ? AgentStatus.ONLINE : AgentStatus.OFFLINE} size={10} />
          )}
        </div>

        {showExpandedView && (
          <div
            style={{
              opacity: 0,
              animation: 'fadeInSlideUp 280ms ease-in forwards',
            }}
            css={css`
              @keyframes fadeInSlideUp {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }
              display: flex;
              justify-content: space-between;
              gap: 10px;
              align-items: center;
              width: calc(100% - 50px);
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
                width: 100%;
                text-align: left;
                min-width: 0px;
              `}
            >
              <QSTypography
                css={css`
                  font-weight: 600;
                  font-size: 14px;
                  letter-spacing: 0%;
                  color: #2d2d2d;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  width: 100%;
                  min-width: 0px;
                  font-style: normal;
                  line-height: 17px;
                `}
              >
                {name}
              </QSTypography>
              {shouldShowAgentAvailability && (
                <QSTypography
                  css={css`
                    font-weight: 500;
                    font-size: 12px;
                    line-height: 12px;
                    letter-spacing: 0%;
                    min-width: 0px;
                    vertical-align: middle;
                    color: ${isOnline ? statusColor.ONLINE : statusColor.OFFLINE};
                  `}
                >
                  {isOnline ? 'Online' : 'Offline'}
                </QSTypography>
              )}
            </div>
            <ArrowExpandIcon
              color="#7a7a7a"
              size={14}
              style={{
                transform: 'rotate(90deg)',
              }}
            />
          </div>
        )}
      </ButtonBase>
      {isMoreMenuOpen && (
        <>
          {/* Full page overlay */}
          <div
            css={css`
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: transparent;
              z-index: 999;
            `}
            onClick={(e) => {
              e.stopPropagation();
              setIsMoreMenuOpen(false);
            }}
          />
          {/* Popover menu */}
          <div
            css={css`
              width: 258px;
              border-radius: 8px;
              border-width: 1px;
              border-color: #e0e0e0;
              border-style: solid;
              position: absolute;
              bottom: 30px;
              right: -10px;
              transform: translateX(calc(100% - 10px));
              background-color: #fff;
              border: 1px solid #e0e0e9;
              box-shadow: 0px 2px 10px 0px #00000026;
              z-index: 1000;
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              css={css`
                display: flex;
                gap: 12px;
                padding: 16px;
              `}
            >
              <div
                css={css({
                  position: 'relative',
                })}
              >
                <ChatProfileAvatar
                  chatType={ChatType.INDIVIDUAL}
                  name={name}
                  phoneNumber={phoneNumber}
                  styles={{
                    height: 40,
                    width: 40,
                    margin: 0,
                  }}
                />
                {shouldShowAgentAvailability && (
                  <StatusIndicator status={isOnline ? AgentStatus.ONLINE : AgentStatus.OFFLINE} size={10} />
                )}
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                  width: 100%;
                  text-align: left;
                `}
              >
                <QSTypography
                  css={css`
                    color: #2d2d2d;
                    font-feature-settings: 'liga' off, 'clig' off;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: normal;
                  `}
                >
                  {name}
                </QSTypography>
                <QSTypography
                  css={css`
                    color: #7a7a7a;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 12px; /* 100% */
                  `}
                >
                  {phoneNumber}
                </QSTypography>
              </div>
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              {shouldShowAgentAvailability && (
                <>
                  <QSTypography
                    css={css`
                      color: #7a7a7a;
                      font-feature-settings: 'liga' off, 'clig' off;
                      font-size: 12px;
                      font-style: normal;
                      font-weight: 500;
                      line-height: normal;
                      padding: 8px 16px 4px 16px;
                    `}
                  >
                    Current status
                  </QSTypography>
                  <div
                    onClick={() => changeStatus(!isOnline)}
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 10px;
                      justify-content: space-between;
                      padding: 0 16px;
                      height: 42px;
                      cursor: pointer;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        margin-left: 2px;
                        gap: 8px;
                      `}
                    >
                      <StatusIndicator
                        status={isOnline ? AgentStatus.ONLINE : AgentStatus.OFFLINE}
                        size={10}
                        position="static"
                      />
                      <QSTypography
                        css={css`
                          font-feature-settings: 'liga' off, 'clig' off;
                          font-weight: 600;
                          font-size: 14px;
                          letter-spacing: 0px;
                          vertical-align: middle;
                          color: ${isOnline ? statusColor.ONLINE : statusColor.OFFLINE};
                          font-style: normal;
                          line-height: normal;
                        `}
                      >
                        {isOnline ? 'Online' : 'Offline'}
                      </QSTypography>
                    </div>
                    <Switch
                      size="small"
                      checked={isOnline}
                      onChange={() => changeStatus(!isOnline)}
                      onClick={(e) => e.stopPropagation()}
                      css={css`
                        & .MuiSwitch-track {
                          padding: 0;
                        }
                        & input {
                          width: 80px;
                        }
                      `}
                    />
                  </div>
                  <QSTypography
                    css={css`
                      color: #7a7a7a;
                      font-feature-settings: 'liga' off, 'clig' off;
                      font-size: 12px;
                      font-style: normal;
                      font-weight: 400;
                      line-height: normal;
                      padding: 4px 16px 16px 16px;
                    `}
                  >
                    New chats can be assigned to you when you are online
                  </QSTypography>
                </>
              )}
              <ButtonBase
                onClick={handleEditProfile}
                css={css`
                  font-family: 'Inter', sans-serif;
                  width: 100%;
                  height: 44px;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  justify-content: flex-start;
                  font-weight: 500;
                  font-size: 14px;
                  border-top: 1px solid #e0e0e0;
                  border-bottom: 1px solid #e0e0e0;
                  padding: 0 16px;
                  color: #2d2d2d;
                  transition: background-color 0.2s ease;
                  &:hover {
                    background-color: #f5f5f5;
                  }
                `}
              >
                <UserProfileIcon color="#7A7A7A" size={20} />
                Your profile
              </ButtonBase>
              <ButtonBase
                onClick={handleLogout}
                css={css`
                  font-family: 'Inter', sans-serif;
                  width: 100%;
                  height: 44px;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  justify-content: flex-start;
                  font-weight: 500;
                  font-size: 14px;
                  padding: 0 16px;
                  color: #2d2d2d;
                  transition: background-color 0.2s ease;
                  border-radius: 0px 0px 8px 8px;

                  &:hover {
                    background-color: #f5f5f5;
                  }
                `}
              >
                <LogoutIcon color="#7A7A7A" style={{ width: '20px', height: '20px' }} />
                Logout
              </ButtonBase>
            </div>
          </div>
        </>
      )}
      <LogoutModal open={showLogoutModal} onClose={() => setShowLogoutModal(false)} />
    </>
  );
}
